import { DataProvider } from 'react-admin'
import { supabase } from './supabaseClient'
import { getInvestmentPropertiesAtDate } from '@repo/supabase'

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination
    const { field, order } = params.sort
    
    // Special handling for properties - use RPC function
    if (resource === 'properties') {
      const filter = params.filter || {}
      
      // Get investment slug from filter
      if (!filter.investment_slug) {
        console.error('❌ No investment_slug provided for properties')
        return { data: [], total: 0 }
      }
      
      console.log('🎯 Using RPC for properties with slug:', filter.investment_slug)
      console.log('🔍 All filters:', filter)
      
      try {
        // Call the RPC function to get properties with current prices
        const rpcData = await getInvestmentPropertiesAtDate(
          supabase,
          filter.investment_slug,
          new Date()
        )

        let filteredData = rpcData
        
        // Apply property_type filter if specified
        if (filter.property_type) {
          filteredData = filteredData.filter(item => item.property_type === filter.property_type)
        }
        
        // Apply status filter if specified  
        if (filter.status_code) {
          filteredData = filteredData.filter(item => item.status_code === filter.status_code)
        } else if (filter.status_codes) {
          filteredData = filteredData.filter(item => filter.status_codes.includes(item.status_code))
        }
        
        // Apply title search filter if specified
        if (filter.title_ilike) {
          let searchTerm = filter.title_ilike
          // Handle both React Admin format and our custom format
          if (typeof searchTerm === 'string') {
            searchTerm = searchTerm.replace(/%/g, '').toLowerCase()
          }
          console.log(`🔍 Applying title search for: "${searchTerm}"`)
          const beforeCount = filteredData.length
          filteredData = filteredData.filter(item => {
            const matches = item.title && item.title.toLowerCase().includes(searchTerm)
            if (matches) {
              console.log(`✅ Match: "${item.title}"`)
            }
            return matches
          })
          console.log(`🔍 Search results: ${beforeCount} → ${filteredData.length}`)
        }
        
        // Sort data - default by title alphabetically
        const sortField = field || 'title'
        const sortOrder = order || 'ASC'
        
        filteredData.sort((a, b) => {
          const aVal = a[sortField]
          const bVal = b[sortField]
          if (aVal < bVal) return sortOrder === 'ASC' ? -1 : 1
          if (aVal > bVal) return sortOrder === 'ASC' ? 1 : -1
          return 0
        })
        
        console.log(`📊 RPC returned ${rpcData?.length || 0} total, ${filteredData.length} after filters, showing all ${filteredData.length}`)
        if (filter.title_ilike) {
          console.log(`🔍 Search applied for: "${filter.title_ilike.replace(/%/g, '')}"`)
        }

        return {
          data: filteredData,
          total: filteredData.length
        }
      } catch (error) {
        console.error('❌ RPC Error:', error)
        throw new Error(`RPC Error: ${error.message}`)
      }
    }
    
    // Handle other resources normally
    let selectClause = '*'
    if (resource === 'investments') {
      selectClause = '*, tenant:tenants(id, name)'
    }
    
    const query = supabase
      .from(resource)
      .select(selectClause, { count: 'exact' })
      .range((page - 1) * perPage, page * perPage - 1)
      .order(field, { ascending: order === 'ASC' })
    
    if (params.filter) {
      console.log('📋 Applied filters:', params.filter)
      Object.keys(params.filter).forEach(key => {
        console.log('🔍 Standard filter:', key, '=', params.filter[key])
        query.eq(key, params.filter[key])
      })
    }
    
    const { data, count, error } = await query
    
    if (error) {
      throw new Error(error.message)
    }
    
    return {
      data: data || [],
      total: count || 0
    }
  },

  getOne: async (resource, params) => {
    // Same special handling for relationships
    let selectClause = '*'
    if (resource === 'properties') {
      selectClause = `*, 
        stage:stages!inner(id, name, investment_id), 
        property_type_details:property_types(key, label),
        current_price:property_price_versions!inner(total_price, currency, valid_from)`
    } else if (resource === 'investments') {
      selectClause = '*, tenant:tenants(id, name)'
    }

    const query = supabase
      .from(resource)
      .select(selectClause)
      .eq('id', params.id)
    
    // Filter for current prices only
    if (resource === 'properties') {
      query.or('property_price_versions.valid_to.is.null,property_price_versions.valid_to.gt.now()', { foreignTable: 'property_price_versions' })
    }

    const { data, error } = await query.single()
    
    if (error) {
      throw new Error(error.message)
    }
    
    return { data }
  },

  getMany: async (resource, params) => {
    const { data, error } = await supabase
      .from(resource)
      .select('*')
      .in('id', params.ids)
    
    if (error) {
      throw new Error(error.message)
    }
    
    return { data: data || [] }
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination
    const { field, order } = params.sort
    
    const query = supabase
      .from(resource)
      .select('*', { count: 'exact' })
      .eq(params.target, params.id)
      .range((page - 1) * perPage, page * perPage - 1)
      .order(field, { ascending: order === 'ASC' })
    
    if (params.filter) {
      Object.keys(params.filter).forEach(key => {
        query.eq(key, params.filter[key])
      })
    }
    
    const { data, count, error } = await query
    
    if (error) {
      throw new Error(error.message)
    }
    
    return {
      data: data || [],
      total: count || 0
    }
  },

  create: async (resource, params) => {
    const { data, error } = await supabase
      .from(resource)
      .insert(params.data)
      .select()
      .single()
    
    if (error) {
      throw new Error(error.message)
    }
    
    return { data }
  },

  update: async (resource, params) => {
    const { data, error } = await supabase
      .from(resource)
      .update(params.data)
      .eq('id', params.id)
      .select()
      .single()
    
    if (error) {
      throw new Error(error.message)
    }
    
    return { data }
  },

  updateMany: async (resource, params) => {
    const { data, error } = await supabase
      .from(resource)
      .update(params.data)
      .in('id', params.ids)
      .select()
    
    if (error) {
      throw new Error(error.message)
    }
    
    return { data: params.ids }
  },

  delete: async (resource, params) => {
    const { data, error } = await supabase
      .from(resource)
      .delete()
      .eq('id', params.id)
      .select()
      .single()
    
    if (error) {
      throw new Error(error.message)
    }
    
    return { data }
  },

  deleteMany: async (resource, params) => {
    const { error } = await supabase
      .from(resource)
      .delete()
      .in('id', params.ids)
    
    if (error) {
      throw new Error(error.message)
    }
    
    return { data: params.ids }
  }
}